/**
 * Schedule API - Meetings
 *
 * All meeting-related endpoints: list, daily, section, faculty.
 */

import { z } from 'zod'
import { PaginationMetaSchema } from '../common'
import { OfferingTypeSchema, MeetingTeacherSchema, ExtendedTeacherSchema, EnrichedSectionSchema } from './common'

// ============================================================================
// Base Meeting Item (from Blackbaud)
// ============================================================================

export const BaseMeetingItemSchema = z.object({
  section_id: z.number(),
  section_identifier: z.string(),
  course_title: z.string(),
  group_name: z.string(),
  offering_type: OfferingTypeSchema,
  meeting_date: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  block_id: z.number(),
  block_name: z.string(),
  room_id: z.number(),
  room_name: z.string(),
  room_number: z.number(),
  room_capacity: z.number(),
  room_code: z.string(),
  faculty_user_id: z.number(),
  faculty_name: z.string(),
  faculty_firstname: z.string(),
  faculty_lastname: z.string(),
  attendance_required: z.boolean(),
  attendance_taken: z.boolean(),
  num_absent: z.number(),
  attendance_id: z.number(),
  level_number: z.number(),
  created_date: z.string(),
  modified_date: z.string(),
  last_modified_user_id: z.number(),
  teachers: z.array(ExtendedTeacherSchema)
})

export type BaseMeetingItem = z.infer<typeof BaseMeetingItemSchema>

// ============================================================================
// GET /v1/schedules/meetings
// ============================================================================

export const GetAllMeetingsRequestSchema = z.object({
  school_id: z.string(),
  start_date: z.string(),
  end_date: z.string().optional(),
  offering_types: z.string().optional(),
  section_ids: z.string().optional(),
  last_modified: z.string().optional(),
  show_time_for_current_date: z.boolean().optional(),
  limit: z.number().optional(),
  offset: z.number().optional()
})

export type GetAllMeetingsRequest = z.infer<typeof GetAllMeetingsRequestSchema>

export const GetAllMeetingsResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    meetings: z.array(BaseMeetingItemSchema),
    sections: z.array(EnrichedSectionSchema),
    meta: PaginationMetaSchema.extend({
      hasNextPage: z.boolean().optional()
    })
  })
})

export type GetAllMeetingsResponse = z.infer<typeof GetAllMeetingsResponseSchema>

// ============================================================================
// Daily Meeting Item
// ============================================================================

export const DailyMeetingItemSchema = z.object({
  section_id: z.number(),
  section_identifier: z.string(),
  course_title: z.string(),
  offering_type: OfferingTypeSchema,
  meeting_date: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  block_id: z.number(),
  block_name: z.string(),
  room_id: z.number().nullable(),
  room_name: z.string(),
  room_number: z.number(),
  room_capacity: z.number().nullable(),
  room_code: z.string(),
  faculty_user_id: z.number().nullable(),
  faculty_name: z.string(),
  teachers: z.array(MeetingTeacherSchema),
  attendance_required: z.boolean(),
  attendance_taken: z.boolean(),
  num_absent: z.number(),
  attendance_id: z.number(),
  timeSlot: z.string(),
  duration: z.number(),
  dayOfWeek: z.string()
})

export type DailyMeetingItem = z.infer<typeof DailyMeetingItemSchema>

export const TimeBlockSchema = z.object({
  startTime: z.string(),
  endTime: z.string(),
  timeSlot: z.string(),
  duration: z.number(),
  meetings: z.array(DailyMeetingItemSchema),
  blockName: z.string().optional(),
  blockId: z.number().optional()
})

export type TimeBlock = z.infer<typeof TimeBlockSchema>

export const OfferingTypeGroupSchema = z.object({
  offeringType: OfferingTypeSchema,
  meetings: z.array(DailyMeetingItemSchema),
  timeBlocks: z.array(TimeBlockSchema),
  summary: z.object({
    totalMeetings: z.number(),
    totalSections: z.number(),
    timeSpan: z.object({
      earliest: z.string(),
      latest: z.string()
    })
  })
})

export type OfferingTypeGroup = z.infer<typeof OfferingTypeGroupSchema>

export const DailySummarySchema = z.object({
  totalMeetings: z.number(),
  totalSections: z.number(),
  offeringTypeCounts: z.record(z.string(), z.number()),
  timeSpan: z.object({
    earliest: z.string(),
    latest: z.string()
  })
})

export type DailySummary = z.infer<typeof DailySummarySchema>

// ============================================================================
// GET /v1/schedules/meetings/daily/:date
// ============================================================================

export const GetDailyScheduleRequestSchema = z.object({
  date: z.string(),
  school_id: z.string(),
  offering_types: z.string().optional(),
  group_by_offering_type: z.boolean().optional(),
  limit: z.number().optional(),
  offset: z.number().optional()
})

export type GetDailyScheduleRequest = z.infer<typeof GetDailyScheduleRequestSchema>

export const GetDailyScheduleResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    date: z.string(),
    dayOfWeek: z.string(),
    meetings: z.array(DailyMeetingItemSchema),
    sections: z.array(EnrichedSectionSchema),
    timeBlocks: z.array(TimeBlockSchema),
    offeringTypeGroups: z.array(OfferingTypeGroupSchema).optional(),
    summary: DailySummarySchema,
    meta: PaginationMetaSchema.extend({
      hasNextPage: z.boolean().optional()
    })
  })
})

export type GetDailyScheduleResponse = z.infer<typeof GetDailyScheduleResponseSchema>

// ============================================================================
// Section Meeting Item
// ============================================================================

export const SectionMeetingItemSchema = z.object({
  section_id: z.number(),
  section_identifier: z.string(),
  course_title: z.string(),
  offering_type: OfferingTypeSchema,
  meeting_date: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  block_id: z.number(),
  block_name: z.string(),
  room_id: z.number().nullable(),
  room_name: z.string(),
  room_number: z.number(),
  room_capacity: z.number().nullable(),
  room_code: z.string(),
  faculty_user_id: z.number().nullable(),
  faculty_name: z.string(),
  teachers: z.array(MeetingTeacherSchema),
  attendance_required: z.boolean(),
  attendance_taken: z.boolean(),
  num_absent: z.number(),
  attendance_id: z.number(),
  dayOfWeek: z.string(),
  timeSlot: z.string(),
  duration: z.number()
})

export type SectionMeetingItem = z.infer<typeof SectionMeetingItemSchema>

export const SectionSummarySchema = z.object({
  totalMeetings: z.number(),
  dateRange: z.object({
    start: z.string(),
    end: z.string()
  }),
  daysOfWeek: z.array(z.string()),
  timeSlots: z.array(
    z.object({
      startTime: z.string(),
      endTime: z.string(),
      dayOfWeek: z.string(),
      occurrences: z.number()
    })
  ),
  rooms: z.array(
    z.object({
      roomId: z.number(),
      roomName: z.string(),
      roomNumber: z.string(),
      occurrences: z.number()
    })
  )
})

export type SectionSummary = z.infer<typeof SectionSummarySchema>

// ============================================================================
// GET /v1/schedules/meetings/section/:section_id
// ============================================================================

export const GetSectionScheduleRequestSchema = z.object({
  section_id: z.string(),
  school_id: z.string(),
  start_date: z.string(),
  end_date: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional()
})

export type GetSectionScheduleRequest = z.infer<typeof GetSectionScheduleRequestSchema>

export const GetSectionScheduleResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    meetings: z.array(SectionMeetingItemSchema),
    sections: z.array(EnrichedSectionSchema),
    meta: PaginationMetaSchema.extend({
      hasNextPage: z.boolean().optional(),
      sectionsRequested: z.number(),
      sectionsFound: z.number()
    })
  })
})

export type GetSectionScheduleResponse = z.infer<typeof GetSectionScheduleResponseSchema>

// ============================================================================
// Faculty Schedule Event
// ============================================================================

export const FacultyScheduleEventSchema = z.object({
  id: z.string(),
  platformId: z.string(),
  meetingDate: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  blockId: z.number(),
  blockName: z.string(),
  roomId: z.number().nullable(),
  roomName: z.string(),
  roomNumber: z.string(),
  roomCapacity: z.number().nullable(),
  roomCode: z.string(),
  attendanceRequired: z.boolean(),
  attendanceTaken: z.boolean(),
  numAbsent: z.number(),
  attendanceId: z.number(),
  isHeadTeacher: z.boolean(),
  teacherRole: z.enum(['head', 'assistant']),
  /**
   * Whether the REQUESTING caller may take attendance for this meeting from the
   * app (plan 2026-08-07-001, R29/R24).
   *
   * Served from the same predicate the write endpoint authorizes on, so the
   * affordance and the gate cannot disagree. True requires all three: the caller
   * leads this section (by `staffRole` or a leadership flag — NOT by the
   * membership role enum, which would exclude coaches), Blackbaud flags the
   * meeting as requiring attendance, and it is dated today or earlier in the
   * school's own zone.
   *
   * It says nothing about the school's three-state setting or its beta gate —
   * those come from app config, and the client ANDs them with this. Optional so
   * an older payload parses; absent reads as not eligible.
   */
  attendanceEligible: z.boolean().optional()
})

export type FacultyScheduleEvent = z.infer<typeof FacultyScheduleEventSchema>

export const FacultyInfoSchema = z.object({
  id: z.number().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  totalMeetings: z.number(),
  headTeacherMeetings: z.number(),
  assistantTeacherMeetings: z.number()
})

export type FacultyInfo = z.infer<typeof FacultyInfoSchema>

// ============================================================================
// GET /v1/schedules/meetings/faculty/:teacher_id
// ============================================================================

export const GetFacultyScheduleRequestSchema = z.object({
  teacher_id: z.string(),
  start_date: z.string(),
  end_date: z.string().optional(),
  offering_types: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional()
})

export type GetFacultyScheduleRequest = z.infer<typeof GetFacultyScheduleRequestSchema>

export const GetFacultyScheduleResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    schedule: z.array(FacultyScheduleEventSchema),
    sections: z.array(EnrichedSectionSchema),
    facultyInfo: FacultyInfoSchema,
    meta: PaginationMetaSchema,
    schoolTimeZone: z.string().nullable()
  }),
  meta: PaginationMetaSchema
})

export type GetFacultyScheduleResponse = z.infer<typeof GetFacultyScheduleResponseSchema>
